import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase/firebaseConfig";
import {
  getDocs,
  collection,
  where,
  query,
} from "firebase/firestore";
import { useContext } from "react";
import { UserContext } from "../context/UserContext";
import {
  Container,
  Table,
  Segment,
  Input,
  Label,
} from "semantic-ui-react";
import { Subtitulo } from "../elementos/General";
import moment from "moment";

const TablaInfo = ({
  ingreso,
  presupuestoCab,
  id_cab_pre,
}) => {
  const { users } = useContext(UserContext);

  const [presupuesto, setPresupuesto] =
    useState([]);

  // =========================================================
  // CONSULTAR PRESUPUESTO
  // =========================================================
  const consultarPresupuesto = async () => {
    const pre = query(
      collection(db, "presupuestos"),
      where("emp_id", "==", users.emp_id),
      where(
        "id_cab_pre",
        "==",
        id_cab_pre
      )
    );

    const presu = await getDocs(pre);

    const existePresupuesto =
      presu.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

    setPresupuesto(existePresupuesto);
  };

  // =========================================================
  // PRESUPUESTO ORDENADO
  // =========================================================
  const presupuestoOrdenado = useMemo(() => {
    return [...presupuesto].sort(
      (a, b) => {
        const fechaA =
          a?.fechaadd?.toMillis?.() ??
          a?.fechaadd?.getTime?.() ??
          0;

        const fechaB =
          b?.fechaadd?.toMillis?.() ??
          b?.fechaadd?.getTime?.() ??
          0;

        return fechaA - fechaB;
      }
    );
  }, [presupuesto]);

  // =========================================================
  // TIPO DE PRESUPUESTO
  // =========================================================
  const esCorrectivaContrato =
    ingreso?.origen_ingreso ===
      "TRASPASO_ST_INTERNO" &&
    ingreso?.servicio === "PRESUPUESTO";

  // =========================================================
  // FALLAS
  // =========================================================
  const falla = useMemo(() => {
    return presupuestoOrdenado.filter(
      (item) =>
        item.categoria === "FALLA"
    );
  }, [presupuestoOrdenado]);

  // =========================================================
  // SERVICIOS + REPUESTOS
  // =========================================================
  const presu = useMemo(() => {
    return presupuestoOrdenado.filter(
      (item) =>
        item.categoria === "REPUESTO" ||
        item.categoria === "SERVICIO"
    );
  }, [presupuestoOrdenado]);

  // =========================================================
  // VALOR ECONÓMICO REAL
  // =========================================================
  const totalReal = useMemo(() => {
    return presu.reduce(
      (acc, item) =>
        acc +
        Number(item.price ?? 0),
      0
    );
  }, [presu]);

  // =========================================================
  // SERVICIOS CUBIERTOS POR CONTRATO
  // =========================================================
  const totalCubiertoContrato =
    useMemo(() => {
      if (!esCorrectivaContrato) {
        return 0;
      }

      return presu
        .filter(
          (item) =>
            item.categoria ===
            "SERVICIO"
        )
        .reduce(
          (acc, item) =>
            acc +
            Number(
              item.price ?? 0
            ),
          0
        );
    }, [
      presu,
      esCorrectivaContrato,
    ]);

  // =========================================================
  // TOTAL QUE DEBE PAGAR EL CLIENTE
  // =========================================================
  const totalPagar = useMemo(() => {
    if (!esCorrectivaContrato) {
      return totalReal;
    }

    return presu
      .filter(
        (item) =>
          item.categoria ===
          "REPUESTO"
      )
      .reduce(
        (acc, item) =>
          acc +
          Number(
            item.price ?? 0
          ),
        0
      );
  }, [
    presu,
    esCorrectivaContrato,
    totalReal,
  ]);

  // =========================================================
  // FECHA
  // =========================================================
  const formatearFecha = (fecha) => {
    if (!fecha) {
      return "";
    }

    const dateObj =
      typeof fecha.toDate ===
      "function"
        ? fecha.toDate()
        : fecha;

    return moment(
      dateObj
    ).format(
      "DD/MM/YYYY HH:mm"
    );
  };

  // =========================================================
  // CARGA INICIAL
  // =========================================================
  useEffect(() => {
    if (
      !users?.emp_id ||
      !id_cab_pre
    ) {
      return;
    }

    consultarPresupuesto();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    users?.emp_id,
    id_cab_pre,
  ]);

  return (
    <>
      <Container>
        {/* =====================================================
            INFORMACIÓN CLIENTE
        ====================================================== */}
        <Subtitulo
          style={{
            fontSize: "18px",
          }}
        >
          Información Cliente
        </Subtitulo>

        <Table
          singleLine
          style={{
            fontSize: "12px",
            lineHeight: "8px",
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                Folio
              </Table.HeaderCell>

              <Table.HeaderCell>
                Rut
              </Table.HeaderCell>

              <Table.HeaderCell>
                Nombre
              </Table.HeaderCell>

              <Table.HeaderCell>
                Fecha
              </Table.HeaderCell>

              <Table.HeaderCell>
                Teléfono
              </Table.HeaderCell>

              <Table.HeaderCell>
                Dirección
              </Table.HeaderCell>

              <Table.HeaderCell>
                Email
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell>
                {ingreso.folio}
              </Table.Cell>

              <Table.Cell>
                {ingreso.rut}
              </Table.Cell>

              <Table.Cell>
                {ingreso.entidad}
              </Table.Cell>

              <Table.Cell>
                {ingreso.date
                  ? formatearFecha(
                      ingreso.date
                    )
                  : "00/00/00 00:00"}
              </Table.Cell>

              <Table.Cell>
                {ingreso.telefono}
              </Table.Cell>

              <Table.Cell>
                {ingreso.direccion}
              </Table.Cell>

              <Table.Cell>
                {ingreso.correo}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        {/* =====================================================
            INFORMACIÓN EQUIPO
        ====================================================== */}
        <Subtitulo
          style={{
            fontSize: "18px",
          }}
        >
          Información Equipo
        </Subtitulo>

        <Table
          singleLine
          style={{
            fontSize: "12px",
            lineHeight: "8px",
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                Familia
              </Table.HeaderCell>

              <Table.HeaderCell>
                Tipo Equipamiento
              </Table.HeaderCell>

              <Table.HeaderCell>
                Marca
              </Table.HeaderCell>

              <Table.HeaderCell>
                Modelo
              </Table.HeaderCell>

              <Table.HeaderCell>
                Serie
              </Table.HeaderCell>

              <Table.HeaderCell>
                Servicio
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell>
                {ingreso.familia}
              </Table.Cell>

              <Table.Cell>
                {ingreso.tipo}
              </Table.Cell>

              <Table.Cell>
                {ingreso.marca}
              </Table.Cell>

              <Table.Cell>
                {ingreso.modelo}
              </Table.Cell>

              <Table.Cell>
                {ingreso.serie}
              </Table.Cell>

              <Table.Cell>
                {ingreso.servicio}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        {/* =====================================================
            PRESUPUESTO
        ====================================================== */}
        {presupuestoCab.length >
        0 ? (
          <>
            {/* =================================================
                FALLAS
            ================================================== */}
            <Subtitulo
              style={{
                fontSize: "18px",
              }}
            >
              Falla(s) Diagnosticada(s)
            </Subtitulo>

            <Table
              singleLine
              style={{
                fontSize: "12px",
                lineHeight: "8px",
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    style={{
                      width: "70px",
                    }}
                  >
                    N°
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Falla diagnosticada
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {falla.map(
                  (
                    item,
                    index
                  ) => {
                    return (
                      <Table.Row
                        key={
                          item.id ||
                          index
                        }
                      >
                        <Table.Cell>
                          {index +
                            1}
                        </Table.Cell>

                        <Table.Cell
                          style={{
                            whiteSpace:
                              "normal",
                            wordWrap:
                              "break-word",
                            fontWeight:
                              "600",
                          }}
                        >
                          {
                            item.item
                          }
                        </Table.Cell>
                      </Table.Row>
                    );
                  }
                )}
              </Table.Body>
            </Table>

            {/* =================================================
                PRESUPUESTO ECONÓMICO
            ================================================== */}
            <Subtitulo
              style={{
                fontSize: "18px",
              }}
            >
              Presupuesto
            </Subtitulo>

            <Table
              celled
              style={{
                fontSize: "12px",
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    style={{
                      width: "60px",
                    }}
                  >
                    N°
                  </Table.HeaderCell>

                  <Table.HeaderCell>
                    Item
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={{
                      width: "150px",
                    }}
                  >
                    Categoría
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    textAlign="right"
                    style={{
                      width: "150px",
                    }}
                  >
                    Valor
                  </Table.HeaderCell>

                  {esCorrectivaContrato && (
                    <Table.HeaderCell
                      style={{
                        width: "210px",
                      }}
                    >
                      Condición
                    </Table.HeaderCell>
                  )}
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {presu.map(
                  (
                    item,
                    index
                  ) => {
                    const esServicio =
                      item.categoria ===
                      "SERVICIO";

                    const esRepuesto =
                      item.categoria ===
                      "REPUESTO";

                    return (
                      <Table.Row
                        key={
                          item.id ||
                          index
                        }
                      >
                        <Table.Cell>
                          {index +
                            1}
                        </Table.Cell>

                        <Table.Cell
                          style={{
                            whiteSpace:
                              "normal",
                            wordWrap:
                              "break-word",
                          }}
                        >
                          {
                            item.item
                          }
                        </Table.Cell>

                        <Table.Cell>
                          {
                            item.categoria
                          }
                        </Table.Cell>

                        <Table.Cell
                          textAlign="right"
                          style={{
                            fontWeight:
                              "600",
                          }}
                        >
                          $
                          {Number(
                            item.price ??
                              0
                          ).toLocaleString()}
                          .-
                        </Table.Cell>

                        {esCorrectivaContrato && (
                          <Table.Cell>
                            {esServicio ? (
                              <Label
                                color="teal"
                                basic
                              >
                                Cubierto
                                por
                                contrato
                              </Label>
                            ) : esRepuesto ? (
                              <Label
                                color="orange"
                                basic
                              >
                                A pagar
                              </Label>
                            ) : null}
                          </Table.Cell>
                        )}
                      </Table.Row>
                    );
                  }
                )}
              </Table.Body>

              <Table.Footer>
                {esCorrectivaContrato ? (
                  <>
                    <Table.Row>
                      <Table.HeaderCell
                        colSpan="4"
                        style={{
                          textAlign:
                            "right",
                          fontWeight:
                            "bold",
                        }}
                      >
                        Valor total
                        servicios y
                        repuestos
                      </Table.HeaderCell>

                      <Table.HeaderCell
                        textAlign="right"
                        style={{
                          fontWeight:
                            "bold",
                        }}
                      >
                        $
                        {totalReal.toLocaleString()}
                        .-
                      </Table.HeaderCell>
                    </Table.Row>

                    <Table.Row
                      positive
                    >
                      <Table.HeaderCell
                        colSpan="4"
                        style={{
                          textAlign:
                            "right",
                          fontWeight:
                            "bold",
                          color:
                            "#00a89d",
                        }}
                      >
                        Servicios
                        cubiertos por
                        contrato
                      </Table.HeaderCell>

                      <Table.HeaderCell
                        textAlign="right"
                        style={{
                          fontWeight:
                            "bold",
                          color:
                            "#00a89d",
                        }}
                      >
                        -$
                        {totalCubiertoContrato.toLocaleString()}
                        .-
                      </Table.HeaderCell>
                    </Table.Row>

                    <Table.Row>
                      <Table.HeaderCell
                        colSpan="4"
                        style={{
                          textAlign:
                            "right",
                          fontSize:
                            "16px",
                          fontWeight:
                            "bold",
                        }}
                      >
                        TOTAL A PAGAR
                      </Table.HeaderCell>

                      <Table.HeaderCell
                        textAlign="right"
                        style={{
                          fontSize:
                            "16px",
                          fontWeight:
                            "bold",
                        }}
                      >
                        $
                        {totalPagar.toLocaleString()}
                        .-
                      </Table.HeaderCell>
                    </Table.Row>
                  </>
                ) : (
                  <Table.Row>
                    <Table.HeaderCell
                      colSpan="3"
                      style={{
                        textAlign:
                          "center",
                        fontSize:
                          "16px",
                        fontWeight:
                          "bold",
                      }}
                    >
                      Total
                    </Table.HeaderCell>

                    <Table.HeaderCell
                      style={{
                        fontSize:
                          "16px",
                        fontWeight:
                          "bold",
                        textAlign:
                          "right",
                      }}
                    >
                      $
                      {totalReal.toLocaleString()}
                      .-
                    </Table.HeaderCell>
                  </Table.Row>
                )}
              </Table.Footer>
            </Table>

            {/* =================================================
                OBSERVACIONES
            ================================================== */}
            <Segment basic>
              <Input
                label="Observaciones: "
                disabled
                style={{
                  width: "100%",
                  height: "60px",
                }}
                type="text"
                name="descripcion"
                placeholder="Ingrese observacion o detalles adicionales"
                value={
                  presupuestoCab?.[0]
                    ?.obs ?? ""
                }
              />
            </Segment>
          </>
        ) : (
          ""
        )}
      </Container>
    </>
  );
};

export default TablaInfo;